import 'twin.macro';
import React, { useState } from 'react';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import Toggle from './Toggle';
import { Plans } from './Plans';
import { Recurrence } from '@teetsh/app/src/containers/Pricing/types';
import { BillingInfos } from '@teetsh/app/src/types/user';
import { useNavigate } from 'react-router-dom';
import { useBillingInfosData } from 'queries/useBillingInfos';
import { useGetPlans } from 'queries/plan/useGetPlans';
import { useTranslation } from 'react-i18next';
import { PricingPageWrapper } from '../PricingPageWrapper';
import Button from '@teetsh/app/src/components/Buttons/Button';
import subscriptionUrl from '@teetsh/app/src/containers/UserSettings/Souscription/urls';
import tw from 'twin.macro';

function isAnUpgrade(billingInfo: BillingInfos) {
  return (
    billingInfo.subscriptionStatus === 'active' &&
    !billingInfo?.isOnFreeOrTrialPlan
  );
}

export default function ChoosePlanPage() {
  const user = useUser();
  const { t } = useTranslation(['userSettings', 'common']);
  const navigate = useNavigate();
  const billingInfos = useBillingInfosData();
  const { data: plans } = useGetPlans(user.locale);

  const queryParams = new URLSearchParams(location.search);
  const isUpdatePlan =
    queryParams.get('change-plan') === 'true' ||
    (billingInfos && isAnUpgrade(billingInfos));

  const defaultRecurrence = isUpdatePlan ? user.recurrence : 'MONTHLY';
  const [recurrence, setRecurrence] = useState<Recurrence>(
    billingInfos?.hasAccessToOldPricing ? 'YEARLY' : defaultRecurrence,
  );
  const disableSwitchRecurrence = !!billingInfos?.hasAccessToOldPricing;
  if (!billingInfos) {
    return null;
  }

  const onChange = (plan: string) => {
    const couponParam = queryParams.get('coupon');
    const params = new URLSearchParams({ plan, recurrence });
    if (couponParam) {
      params.set('coupon', couponParam);
    }
    navigate(`/subscription/address?${params.toString()}`);
  };

  return (
    <PricingPageWrapper>
      <div tw="pt-12 -mb-16">
        <div tw="relative max-w-2xl mx-auto px-4 text-center sm:px-6 lg:max-w-7xl lg:px-8">
          <div tw="display text-white">
            <span tw="block lg:inline">
              {t('userSettings:subscription.pricing.choosePlan')}
            </span>
          </div>
          {!disableSwitchRecurrence && !isUpdatePlan && (
            <p tw="mt-2 text-reverse-moderate">
              {t('userSettings:subscription.pricing.billedMonthlyOrYearly')}
            </p>
          )}
        </div>

        {/* Toggle */}
        {!disableSwitchRecurrence && !isUpdatePlan && (
          <div tw="mt-10">
            <Toggle value={recurrence} onChange={b => setRecurrence(b)} />
          </div>
        )}
        <div tw=" pb-12 sm:pb-16 lg:pb-24 mt-8">
          <Plans
            plans={plans || []}
            isOnOldPricing={billingInfos.isOnOldPricing}
            currentPlan={user.currentPlan}
            paidRecurrence={recurrence}
            shownRecurrence="MONTHLY"
            renderControls={({ tier, isCurrentPlan }) => (
              <div>
                {!isCurrentPlan && (
                  <Button
                    aria-label={`Choisir l'abonnement ${tier.label}`}
                    intent="primary"
                    fill
                    extralarge
                    onClick={() => {
                      if (tier.id === 'free' && user.currentPlan === 'trial') {
                        navigate(
                          `${subscriptionUrl.subscription}/?open-already-trial=true`,
                        );
                        return;
                      }
                      if (
                        (tier as any).isFree &&
                        tier.id !== user.currentPlan
                      ) {
                        navigate(subscriptionUrl.cancel());
                        return;
                      }
                      onChange(tier.id);
                    }}
                  >
                    {t('common:buttons.choose')}
                  </Button>
                )}
                {isCurrentPlan && (
                  <p
                    // le py-1.5 h-10 c'est pour aligner avec les boutons
                    tw="mt-2 text-sm text-neutral-quiet text-center py-1.5 h-10"
                  >
                    {t('userSettings:subscription.pricing.currentPlan')}
                  </p>
                )}

                {recurrence === 'YEARLY' && (
                  <div
                    css={[
                      tw`text-center mt-2 text-sm text-neutral-quiet`,
                      tier.priceMonthly === 0 && tw`invisible`,
                    ]}
                  >
                    {t('userSettings:subscription.pricing.billedYearlyBy', {
                      amount: tier.priceYearly,
                    })}
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </PricingPageWrapper>
  );
}
